import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GripVerticalIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react'
import {
	Button,
	IconButton,
	Input,
	Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
	Textarea,
} from '@rentflowdev/ui'
import type { BlockType, FieldType, TemplateBlock } from './types'
import { FIELD_TYPE_LABELS, FIELD_TYPES } from './types'
import { FIELD_VARIABLE_GROUPS, FIELD_VARIABLES, TABLE_VARIABLES, TABLE_VARIABLES_BY_KEY } from './field-variables'

function generateId() {
	return crypto.randomUUID()
}

interface TemplateEditorProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	templateName: string
	baseTemplateName: string
	initialBlocks?: TemplateBlock[]
	onSave?: (blocks: TemplateBlock[]) => void | Promise<void>
	isSaving?: boolean
}

type EditingState =
	| { kind: 'none' }
	| { kind: 'text'; blockId: string }
	| { kind: 'section'; blockId: string }
	| { kind: 'field'; blockId: string }
	| { kind: 'table'; blockId: string }

export function TemplateEditor({
	open,
	onOpenChange,
	templateName,
	baseTemplateName,
	initialBlocks = [],
	onSave,
	isSaving = false,
}: TemplateEditorProps) {
	const { t } = useTranslation()
	const [blocks, setBlocks] = useState<TemplateBlock[]>(initialBlocks)
	const [editing, setEditing] = useState<EditingState>({ kind: 'none' })
	const [addMenuOpen, setAddMenuOpen] = useState(false)
	const addMenuRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (open) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setBlocks(initialBlocks)
			 
			setEditing({ kind: 'none' })
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
				setAddMenuOpen(false)
			}
		}
		if (addMenuOpen) document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [addMenuOpen])

	const updateBlock = useCallback((id: string, updates: Partial<TemplateBlock>) => {
		setBlocks((prev) => prev.map((b) => b.id === id ? { ...b, ...updates } : b))
	}, [])

	const deleteBlock = useCallback((id: string) => {
		setBlocks((prev) => prev.filter((b) => b.id !== id))
		setEditing({ kind: 'none' })
	}, [])

	const addBlock = useCallback((type: BlockType, fieldType?: FieldType) => {
		const newBlock: TemplateBlock = {
			id: generateId(),
			type,
			content: '',
			fieldType,
			blockOrder: blocks.length,
		}
		setBlocks((prev) => [...prev, newBlock])
		setAddMenuOpen(false)
		if (type === 'FIELD') setEditing({ kind: 'field', blockId: newBlock.id })
		else if (type === 'TABLE') setEditing({ kind: 'table', blockId: newBlock.id })
		else if (type === 'TEXT') setEditing({ kind: 'text', blockId: newBlock.id })
		else setEditing({ kind: 'section', blockId: newBlock.id })
	}, [blocks.length])

	async function handleSave() {
		try {
			await onSave?.(blocks)
		} catch {
			return
		}
		onOpenChange(false)
	}

	if (!open) return null

	const selectedBlock = editing.kind !== 'none'
		? blocks.find((b) => b.id === editing.blockId)
		: undefined

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="flex h-[90vh] w-[90vw] max-w-[1100px] flex-col overflow-hidden rounded-lg bg-(--color-background) shadow-lg">
				{/* Header */}
				<div className="flex items-start justify-between border-b border-(--color-border) px-6 py-5">
					<div>
						<h2 className="text-xl font-semibold text-(--color-foreground)">
							{t('documents.editor.title', { name: templateName })}
						</h2>
						<p className="text-sm text-(--color-muted-foreground)">
							{t('documents.editor.baseTemplate')}: {baseTemplateName}
						</p>
					</div>
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="rounded-(--radius-sm) p-1 text-(--color-muted-foreground) hover:text-(--color-foreground) transition-colors"
					>
						<XIcon className="size-5" />
					</button>
				</div>

				{/* Body: document view + sidebar */}
				<div className="flex flex-1 overflow-hidden">
					{/* Document canvas */}
					<div
						className="flex-1 overflow-auto p-8 bg-(--color-muted)/20"
						onClick={() => setEditing({ kind: 'none' })}
					>
						<div
							className="mx-auto max-w-[680px] rounded-lg bg-(--color-background) px-10 py-8 shadow-sm min-h-[400px]"
							onClick={(e) => e.stopPropagation()}
						>
							{blocks.length === 0 ? (
								<p className="text-sm text-(--color-muted-foreground) text-center py-16">
									{t('documents.editor.emptyHint', { defaultValue: 'Добавьте блоки с помощью кнопки ниже' })}
								</p>
							) : (
								<div className="flex flex-col gap-1">
									{blocks.map((block, i) => (
										<DocumentBlock
											key={block.id}
											block={block}
											isSelected={editing.kind !== 'none' && editing.blockId === block.id}
											isFirst={i === 0}
											isLast={i === blocks.length - 1}
											onSelect={() => {
												if (block.type === 'FIELD') setEditing({ kind: 'field', blockId: block.id })
												else if (block.type === 'TABLE') setEditing({ kind: 'table', blockId: block.id })
												else if (block.type === 'TEXT') setEditing({ kind: 'text', blockId: block.id })
												else setEditing({ kind: 'section', blockId: block.id })
											}}
											onUpdate={updateBlock}
											onDelete={deleteBlock}
											onMoveUp={() => {
												if (i === 0) return
												setBlocks((prev) => {
													const next = [...prev]
													;[next[i - 1], next[i]] = [next[i], next[i - 1]]
													return next
												})
											}}
											onMoveDown={() => {
												if (i === blocks.length - 1) return
												setBlocks((prev) => {
													const next = [...prev]
													;[next[i], next[i + 1]] = [next[i + 1], next[i]]
													return next
												})
											}}
										/>
									))}
								</div>
							)}

							{/* Add block button */}
							<div className="relative mt-6 flex justify-center" ref={addMenuRef}>
								<button
									type="button"
									onClick={() => setAddMenuOpen((v) => !v)}
									className="flex items-center gap-1.5 rounded-(--radius-md) border border-dashed border-(--color-border) px-4 py-2 text-sm text-(--color-muted-foreground) hover:border-(--color-primary) hover:text-(--color-primary) transition-colors"
								>
									<PlusIcon className="size-4" />
									{t('documents.editor.addElement', { defaultValue: 'Добавить блок' })}
								</button>
								{addMenuOpen && (
									<div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-10 w-52 rounded-(--radius-md) border border-(--color-border) bg-(--color-background) shadow-md overflow-hidden">
										<AddMenuItem label="Заголовок раздела" onClick={() => addBlock('SECTION')} />
										<AddMenuItem label="Подзаголовок" onClick={() => addBlock('SUBSECTION')} />
										<AddMenuItem label={t('documents.editor.text', { defaultValue: 'Текст' })} onClick={() => addBlock('TEXT')} />
										<div className="h-px bg-(--color-border)" />
										{FIELD_TYPES.map((ft) => (
											<AddMenuItem
												key={ft}
												label={FIELD_TYPE_LABELS[ft]}
												onClick={() => addBlock('FIELD', ft)}
											/>
										))}
										<div className="h-px bg-(--color-border)" />
										{TABLE_VARIABLES.map((tv) => (
											<AddMenuItem
												key={tv.key}
												label={tv.label}
												onClick={() => {
													const newBlock: TemplateBlock = {
														id: generateId(),
														type: 'TABLE',
														content: tv.label,
														fieldKey: tv.key,
														blockOrder: blocks.length,
													}
													setBlocks((prev) => [...prev, newBlock])
													setAddMenuOpen(false)
													setEditing({ kind: 'table', blockId: newBlock.id })
												}}
											/>
										))}
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Sidebar: block properties */}
					<div className="w-[280px] shrink-0 overflow-auto border-l border-(--color-border) p-5">
						{selectedBlock ? (
							<BlockPropertiesPanel
								block={selectedBlock}
								onUpdate={updateBlock}
								onDelete={deleteBlock}
								onClose={() => setEditing({ kind: 'none' })}
								t={t}
							/>
						) : (
							<div className="flex flex-col gap-3">
								<h3 className="text-sm font-[500] text-(--color-foreground)">
									{t('documents.editor.hint', { defaultValue: 'Выберите блок для редактирования' })}
								</h3>
								<p className="text-xs text-(--color-muted-foreground)">
									{t('documents.editor.hintDescription', { defaultValue: 'Нажмите на любой блок в документе чтобы изменить его содержимое или настройки переменной.' })}
								</p>
								<div className="mt-2 rounded-(--radius-md) border border-(--color-border) p-3 text-xs text-(--color-muted-foreground)">
									<p className="font-[500] text-(--color-foreground) mb-1">Блоки в документе:</p>
									<p>· <span className="font-[500]">Заголовок / Подзаголовок</span> — структура</p>
									<p>· <span className="font-[500]">Текст</span> — параграф</p>
									<p>· <span className="text-(--color-info) font-[500]">[Переменная]</span> — поле подстановки</p>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Footer */}
				<div className="flex justify-end border-t border-(--color-border) px-6 py-4">
					<Button onClick={handleSave} disabled={isSaving}>
						{t('documents.editor.save')}
					</Button>
				</div>
			</div>
		</div>
	)
}

function AddMenuItem({ label, onClick }: { label: string; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full px-3 py-2 text-left text-sm hover:bg-(--color-muted)/50 transition-colors"
		>
			{label}
		</button>
	)
}

interface DocumentBlockProps {
	block: TemplateBlock
	isSelected: boolean
	isFirst: boolean
	isLast: boolean
	onSelect: () => void
	onUpdate: (id: string, updates: Partial<TemplateBlock>) => void
	onDelete: (id: string) => void
	onMoveUp: () => void
	onMoveDown: () => void
}

function DocumentBlock({ block, isSelected, isFirst, isLast, onSelect, onUpdate: _onUpdate, onDelete, onMoveUp, onMoveDown }: DocumentBlockProps) {
	const blockId = block.id ?? ''

	const variable = block.fieldKey
		? FIELD_VARIABLES.find((v) => v.key === block.fieldKey)
		: undefined

	const variableLabel = variable?.label ?? block.content ?? block.fieldKey ?? '...'

	return (
		<div
			className={`group relative flex items-start gap-2 rounded-(--radius-md) px-2 py-1 cursor-pointer transition-colors ${
				isSelected
					? 'bg-(--color-primary)/5 ring-1 ring-(--color-primary)/30'
					: 'hover:bg-(--color-muted)/40'
			}`}
			onClick={onSelect}
		>
			{/* Drag handle / move buttons */}
			<div className="flex shrink-0 flex-col items-center gap-0.5 pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
				<button
					type="button"
					onClick={(e) => { e.stopPropagation(); onMoveUp() }}
					disabled={isFirst}
					className="flex h-4 w-4 items-center justify-center rounded text-(--color-muted-foreground) hover:text-(--color-foreground) disabled:opacity-30 text-xs leading-none"
					aria-label="Move up"
				>
					▲
				</button>
				<GripVerticalIcon className="size-4 text-(--color-muted-foreground)" />
				<button
					type="button"
					onClick={(e) => { e.stopPropagation(); onMoveDown() }}
					disabled={isLast}
					className="flex h-4 w-4 items-center justify-center rounded text-(--color-muted-foreground) hover:text-(--color-foreground) disabled:opacity-30 text-xs leading-none"
					aria-label="Move down"
				>
					▼
				</button>
			</div>

			{/* Block content */}
			<div className="flex-1 min-w-0 py-0.5">
				{block.type === 'SECTION' && (
					<p className="text-base font-bold text-(--color-foreground) uppercase tracking-wide">
						{block.content || <span className="text-(--color-muted-foreground) italic">Заголовок раздела...</span>}
					</p>
				)}
				{block.type === 'SUBSECTION' && (
					<p className="text-sm font-semibold text-(--color-foreground)">
						{block.content || <span className="text-(--color-muted-foreground) italic">Подзаголовок...</span>}
					</p>
				)}
				{block.type === 'TEXT' && (
					<p className="text-sm leading-relaxed text-(--color-foreground) whitespace-pre-wrap">
						{block.content || <span className="text-(--color-muted-foreground) italic">Текст параграфа...</span>}
					</p>
				)}
				{block.type === 'FIELD' && (
					<span className="inline-flex items-center gap-1.5 rounded-(--radius-sm) bg-(--color-info)/10 px-2 py-0.5 text-sm font-[500] text-(--color-info)">
						<span className="text-(--color-info)/60 text-xs">{'{'}</span>
						{variableLabel}
						<span className="text-(--color-info)/60 text-xs">{'}'}</span>
					</span>
				)}
				{block.type === 'TABLE' && (
					<div className="w-full rounded-(--radius-sm) border border-dashed border-(--color-border) bg-(--color-muted)/30 px-3 py-2">
						<p className="text-xs font-[500] text-(--color-muted-foreground)">
							{block.fieldKey ? (TABLE_VARIABLES_BY_KEY[block.fieldKey]?.label ?? block.fieldKey) : 'Таблица'}
						</p>
						<div className="mt-1.5 grid grid-cols-4 gap-1">
							{[...Array(8)].map((_, i) => (
								<div key={i} className="h-4 rounded bg-(--color-border)/60" />
							))}
						</div>
					</div>
				)}
			</div>

			{/* Delete button */}
			<button
				type="button"
				onClick={(e) => { e.stopPropagation(); onDelete(blockId) }}
				className="shrink-0 rounded-(--radius-sm) p-1 text-(--color-muted-foreground) opacity-0 group-hover:opacity-100 hover:text-(--color-destructive) transition-all"
				aria-label="Delete block"
			>
				<Trash2Icon className="size-3.5" />
			</button>
		</div>
	)
}

function BlockPropertiesPanel({
	block,
	onUpdate,
	onDelete,
	onClose,
	t: _t,
}: {
	block: TemplateBlock
	onUpdate: (id: string, updates: Partial<TemplateBlock>) => void
	onDelete: (id: string) => void
	onClose: () => void
	t: (key: string, opts?: Record<string, unknown>) => string
}) {
	const blockId = block.id ?? ''

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-[500] text-(--color-foreground)">
					{block.type === 'FIELD' ? 'Переменное поле' :
					 block.type === 'TABLE' ? 'Таблица' :
					 block.type === 'TEXT' ? 'Текстовый блок' :
					 block.type === 'SECTION' ? 'Заголовок' : 'Подзаголовок'}
				</h3>
				<IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Close">
					<XIcon className="size-4" />
				</IconButton>
			</div>

			{block.type === 'FIELD' && (
				<>
					<div className="flex flex-col gap-1.5">
						<label className="text-xs font-[500] text-(--color-muted-foreground) uppercase tracking-wide">
							Тип поля
						</label>
						<Select
							value={block.fieldType}
							onValueChange={(v) => onUpdate(blockId, { fieldType: v as FieldType })}
						>
							<SelectTrigger className="h-9 text-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{FIELD_TYPES.map((ft) => (
									<SelectItem key={ft} value={ft}>{FIELD_TYPE_LABELS[ft]}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className="text-xs font-[500] text-(--color-muted-foreground) uppercase tracking-wide">
							Переменная
						</label>
						<Select
							value={block.fieldKey ?? '__none__'}
							onValueChange={(v) => onUpdate(blockId, { fieldKey: v === '__none__' ? undefined : v })}
						>
							<SelectTrigger className="h-9 text-sm">
								<SelectValue placeholder="Без привязки" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="__none__">Без привязки</SelectItem>
								{FIELD_VARIABLE_GROUPS.map((group) => (
									<SelectGroup key={group}>
										<SelectLabel>{group}</SelectLabel>
										{FIELD_VARIABLES.filter((v) => v.group === group).map((v) => (
											<SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>
										))}
									</SelectGroup>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="flex flex-col gap-1.5">
						<label className="text-xs font-[500] text-(--color-muted-foreground) uppercase tracking-wide">
							Метка поля
						</label>
						<Input
							value={block.content}
							placeholder="Метка поля..."
							onChange={(e) => onUpdate(blockId, { content: e.target.value })}
							className="h-9 text-sm"
						/>
					</div>
				</>
			)}

			{block.type === 'TABLE' && (
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-[500] text-(--color-muted-foreground) uppercase tracking-wide">
						Тип таблицы
					</label>
					<Select
						value={block.fieldKey ?? ''}
						onValueChange={(v) => {
							const tv = TABLE_VARIABLES_BY_KEY[v]
							onUpdate(blockId, { fieldKey: v, content: tv?.label ?? v })
						}}
					>
						<SelectTrigger className="h-9 text-sm">
							<SelectValue placeholder="Выберите таблицу" />
						</SelectTrigger>
						<SelectContent>
							{TABLE_VARIABLES.map((tv) => (
								<SelectItem key={tv.key} value={tv.key}>{tv.label}</SelectItem>
							))}
						</SelectContent>
					</Select>
					<p className="text-xs text-(--color-muted-foreground)">
						Таблица заполняется автоматически из данных документа.
					</p>
				</div>
			)}

			{block.type === 'TEXT' && (
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-[500] text-(--color-muted-foreground) uppercase tracking-wide">
						Содержимое
					</label>
					<Textarea
						value={block.content}
						onChange={(e) => onUpdate(blockId, { content: e.target.value })}
						className="min-h-[120px] resize-y text-sm leading-relaxed"
						placeholder="Введите текст..."
						autoFocus
					/>
				</div>
			)}

			{(block.type === 'SECTION' || block.type === 'SUBSECTION') && (
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-[500] text-(--color-muted-foreground) uppercase tracking-wide">
						Заголовок
					</label>
					<Input
						value={block.content}
						onChange={(e) => onUpdate(blockId, { content: e.target.value })}
						className="h-9 text-sm"
						placeholder="Введите заголовок..."
						autoFocus
					/>
				</div>
			)}

			<button
				type="button"
				onClick={() => onDelete(blockId)}
				className="mt-2 flex items-center gap-2 rounded-(--radius-md) px-3 py-2 text-sm text-(--color-destructive) hover:bg-(--color-destructive)/10 transition-colors"
			>
				<Trash2Icon className="size-4" />
				Удалить блок
			</button>
		</div>
	)
}
